import React, { useState, useEffect } from 'react';
import { 
  ShoppingCart, 
  CreditCard, 
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Wallet,
  Receipt,
  FileText,
  Package
} from 'lucide-react';
import dashboardService from '../../services/dashboardService';
import './Dashboard.css';

const Dashboard = ({ toggleSidebar, userRole, userId }) => {
  const [preset, setPreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);

  // Initialize dates - set to empty for 'all' data by default
  useEffect(() => {
    // Leave dates empty to fetch all data
    setDateFrom('');
    setDateTo('');
  }, []);

  // Fetch dashboard data when dates change or on initial load
  useEffect(() => {
    fetchDashboardData();
  }, [dateFrom, dateTo, userRole, userId]);

  // Fetch monthly data for charts
  useEffect(() => {
    fetchMonthlyData();
  }, [userRole, userId]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // If both dates are empty, fetch all data (no date filter)
      const startDate = dateFrom || null;
      const endDate = dateTo || null;
      
      const data = await dashboardService.getStatsWithTrends(
        startDate,
        endDate,
        userRole,
        userId
      );
      setStats(data.current);
      setTrends(data.trends);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyData = async () => {
    try {
      const data = await dashboardService.getMonthlyComparison(6, userRole, userId);
      setMonthlyData(data);
    } catch (error) {
      console.error('Error fetching monthly data:', error);
    }
  };

  const handlePresetChange = (e) => {
    const newPreset = e.target.value;
    setPreset(newPreset);
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    switch(newPreset) {
      case 'all':
        setDateFrom('');
        setDateTo('');
        break;
      case 'today':
        setDateFrom(today.toISOString().split('T')[0]);
        setDateTo(today.toISOString().split('T')[0]);
        break;
      case 'yesterday':
        setDateFrom(yesterday.toISOString().split('T')[0]);
        setDateTo(yesterday.toISOString().split('T')[0]);
        break;
      case 'last7':
        setDateFrom(lastWeek.toISOString().split('T')[0]);
        setDateTo(today.toISOString().split('T')[0]);
        break;
      case 'thisMonth':
        setDateFrom(firstDayOfMonth.toISOString().split('T')[0]);
        setDateTo(today.toISOString().split('T')[0]);
        break;
      default:
        break;
    }
  };

  const formatCurrency = (value) => {
    return "Rs. " + value.toLocaleString('en-us', { 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    });
  };

  const StatCard = ({ title, value, type, trend, icon: Icon, isCount = false }) => {
    if (loading || !stats) {
      return (
        <div className={`stat-card ${type}`}>
          <div className="stat-header">
            <div className="stat-title">{title}</div>
            <Icon className="stat-icon" size={20} />
          </div>
          <div className="stat-value">Loading...</div>
        </div>
      );
    }

    return (
      <div className={`stat-card ${type}`}>
        <div className="stat-header">
          <div className="stat-title">{title}</div>
          <Icon className="stat-icon" size={20} />
        </div>
        <div className="stat-value">
          {isCount ? value : formatCurrency(value)}
        </div>
        {trend && (
          <div className={`stat-trend ${trend.isUp ? 'trend-up' : 'trend-down'}`}>
            {trend.isUp ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            <span>{trend.value}% {trend.isUp ? 'increase' : 'decrease'}</span>
          </div>
        )}
      </div>
    );
  };

  if (loading && !stats) {
    return (
      <main className="inventory-main-content">
        <header className="inventory-content-header">
          <div className="header-left">
            <button onClick={toggleSidebar} className="sidebar-toggle-btn" type="button">
              ☰
            </button>
            <h1 className="page-title">Dashboard</h1>
          </div>
        </header>
        <div className="inventory-loading">
          <div className="spinner-wrapper">
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px'
    }}>
      <div className="inventory-spinner"></div>
      <span>Loading Dashboard data...</span>
    </div>
  </div>
        </div>
        <style>{`
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .inventory-loading {
    text-align: center;
    vertical-align: middle;
  }

  .inventory-loading .spinner-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 120px;
  }

  .inventory-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #E5E7EB;
    border-top: 4px solid #004080;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
`}</style>
      </main>
    );
  }

  return (
    <main className="inventory-main-content">
      {/* Header */}
      <header className="inventory-content-header">
        <div className="header-left">
          <button onClick={toggleSidebar} className="sidebar-toggle-btn" type="button">
            ☰
          </button>
          <h1 className="page-title">Dashboard</h1>
        </div>

        {/* Date Filters */}
        <div className="date-filters">
          <select 
            className="filter-select" 
            value={preset}
            onChange={handlePresetChange}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 Days</option>
            <option value="thisMonth">This Month</option>
            <option value="custom">Custom Range</option>
          </select>

          <div className="date-input-group">
            <input 
              type="date" 
              className="date-input" 
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPreset('custom');
              }}
            />
            <span>to</span>
            <input 
              type="date" 
              className="date-input" 
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPreset('custom');
              }}
            />
          </div>
        </div>
      </header>

      {stats && (
        <>
          {/* Main Dashboard Widgets - 7 Required Widgets */}
          <div className="stats-grid">
            {/* Row 1: 4 widgets */}
            <StatCard
              title="Total Sales"
              value={stats.totalSales}
              type="sales"
              trend={trends?.totalSales}
              icon={ShoppingCart}
            />
            <StatCard
              title="Total Sales (Without Credit)"
              value={stats.totalSalesWithoutCredit}
              type="cash"
              trend={trends?.totalSalesWithoutCredit}
              icon={DollarSign}
            />
            <StatCard
              title="Cash Count"
              value={stats.cashCount}
              type="cash"
              trend={trends?.cashCount}
              icon={DollarSign}
            />
            <StatCard
              title="Cheque Count"
              value={stats.chequeCount}
              type="credit"
              trend={trends?.chequeCount}
              icon={DollarSign}
            />
          </div>

          {/* Row 2: 3 widgets */}
          <div className="stats-grid" style={{ marginTop: '25px', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
            <StatCard
              title="Credit"
              value={stats.credit}
              type="credit"
              trend={trends?.credit}
              icon={CreditCard}
            />
            <StatCard
              title="Collections"
              value={stats.collections}
              type="collections"
              trend={trends?.collections}
              icon={Wallet}
            />
            <StatCard
              title="Expenses"
              value={stats.expenses}
              type="expense"
              trend={trends?.expenses}
              icon={Receipt}
            />
         

          {/* Row 3: 2 new widgets - Unit Cost and Total Profit */}
          
            <StatCard
              title="Total Unit Cost"
              value={stats.unitCost}
              type="unit-cost"
              trend={trends?.unitCost}
              icon={Package}
            />
            <StatCard
              title="Total Profit"
              value={stats.totalProfit}
              type="profit"
              trend={trends?.totalProfit}
              icon={TrendingUp}
            />
          </div>

          {/* Charts Section */}
          <div className="charts-section">
            {/* <h2 className="section-title">
              <BarChart3 size={24} />
              Performance Overview
            </h2>
            
            <div className="charts-grid"> */}
              {/* Sales vs Expenses Chart */}
              <div className="chart-card">
                {/* <div className="chart-header">
                  <h3 className="chart-title">Sales vs Expenses (Last 6 Months)</h3>
                </div>
                <div className="chart-content">
                  <div style={{ overflowX: 'auto' }}> */}
                    {/* <table className="chart-table">
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left' }}>Month</th>
                          <th style={{ textAlign: 'right', color: '#10B981' }}>Sales</th>
                          <th style={{ textAlign: 'right', color: '#EF4444' }}>Expenses</th>
                          <th style={{ textAlign: 'right', color: '#004080' }}>Profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyData.map((item, index) => (
                          <tr key={index}>
                            <td style={{ fontWeight: '500' }}>{item.month}</td>
                            <td style={{ textAlign: 'right', color: '#10B981' }}>
                              {formatCurrency(item.sales)}
                            </td>
                            <td style={{ textAlign: 'right', color: '#EF4444' }}>
                              {formatCurrency(item.expenses)}
                            </td>
                            <td style={{ textAlign: 'right', color: '#004080', fontWeight: '600' }}>
                              {formatCurrency(item.profit)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table> */}
                  {/* </div>
                </div> */}
              {/* </div> */}

              {/* Payment Breakdown */}
              <div className="chart-card">
                <div className="chart-header">
                  <h3 className="chart-title">Payment Breakdown</h3>
                </div>
                <div className="chart-content" style={{ padding: '20px' }}>
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span style={{ fontWeight: '600', color: '#10B981' }}>Cash Sales</span>
                      <span>{formatCurrency(stats.cashCount)}</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ 
                          width: `${stats.totalSales > 0 ? (stats.cashCount / stats.totalSales * 100).toFixed(1) : 0}%`, 
                          background: '#10B981' 
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span style={{ fontWeight: '600', color: '#F59E0B' }}>Cheque Sales</span>
                      <span>{formatCurrency(stats.chequeCount)}</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ 
                          width: `${stats.totalSales > 0 ? (stats.chequeCount / stats.totalSales * 100).toFixed(1) : 0}%`, 
                          background: '#F59E0B' 
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span style={{ fontWeight: '600', color: '#EF4444' }}>Credit Sales</span>
                      <span>{formatCurrency(stats.breakdown.creditSales)}</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ 
                          width: `${stats.totalSales > 0 ? (stats.breakdown.creditSales / stats.totalSales * 100).toFixed(1) : 0}%`, 
                          background: '#EF4444' 
                        }}
                      />
                    </div>
                  </div>

                  <div className="total-payments">
                    <div style={{ fontWeight: '600', color: '#004080' }}>
                      Total Sales: {formatCurrency(stats.totalSales)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </main>
  );
};

export default Dashboard;